import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../mawi-ui';
import { Badge } from '../../mawi-ui';
import { Button } from '../../mawi-ui';
import { ChevronDown, ChevronRight, Plus, Minus, Edit } from 'lucide-react';
import { cn } from '../../utils';

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged' | 'modified';
  content: string;
  lineNumber?: number;
  oldLineNumber?: number;
  newLineNumber?: number;
}

interface DiffHunk {
  header: string;
  lines: DiffLine[];
  startLine: number;
  endLine: number;
}

interface VersionDiffViewerProps {
  oldCode: string;
  newCode: string;
  filename?: string;
  title?: string;
  className?: string;
}

const VersionDiffViewer: React.FC<VersionDiffViewerProps> = ({
  oldCode,
  newCode,
  filename,
  title = 'Code Changes',
  className,
}) => {
  const diffData = useMemo(() => {
    const oldLines = oldCode.split('\n');
    const newLines = newCode.split('\n');

    // Simple diff algorithm - in production, use a proper diff library
    const changes: DiffLine[] = [];
    const maxLines = Math.max(oldLines.length, newLines.length);

    for (let i = 0; i < maxLines; i++) {
      const oldLine = oldLines[i];
      const newLine = newLines[i];

      if (oldLine === undefined && newLine !== undefined) {
        // Added line
        changes.push({
          type: 'added',
          content: newLine,
          newLineNumber: i + 1,
        });
      } else if (oldLine !== undefined && newLine === undefined) {
        // Removed line
        changes.push({
          type: 'removed',
          content: oldLine,
          oldLineNumber: i + 1,
        });
      } else if (oldLine !== newLine) {
        // Modified line
        if (oldLine) {
          changes.push({
            type: 'removed',
            content: oldLine,
            oldLineNumber: i + 1,
          });
        }
        if (newLine) {
          changes.push({
            type: 'added',
            content: newLine,
            newLineNumber: i + 1,
          });
        }
      } else {
        // Unchanged line (show some context)
        if (changes.length > 0 && changes[changes.length - 1].type !== 'unchanged') {
          changes.push({
            type: 'unchanged',
            content: oldLine,
            lineNumber: i + 1,
          });
        }
      }
    }

    // Group into hunks (simplified)
    const hunks: DiffHunk[] = [];
    let currentHunk: DiffLine[] = [];
    let startLine = 0;

    changes.forEach((change, index) => {
      if (change.type !== 'unchanged' && currentHunk.length === 0) {
        startLine = change.lineNumber || change.oldLineNumber || change.newLineNumber || 0;
      }

      currentHunk.push(change);

      // If we have 3 unchanged lines, create a new hunk
      if (change.type === 'unchanged' && currentHunk.filter(c => c.type === 'unchanged').length >= 3) {
        if (currentHunk.length > 3) {
          hunks.push({
            header: `@@ -${startLine},${currentHunk.length} +${startLine},${currentHunk.length} @@`,
            lines: currentHunk,
            startLine,
            endLine: startLine + currentHunk.length - 1,
          });
        }
        currentHunk = [];
      }
    });

    // Add remaining hunk
    if (currentHunk.length > 0) {
      hunks.push({
        header: `@@ -${startLine},${currentHunk.length} +${startLine},${currentHunk.length} @@`,
        lines: currentHunk,
        startLine,
        endLine: startLine + currentHunk.length - 1,
      });
    }

    return { changes, hunks };
  }, [oldCode, newCode]);

  const getLineIcon = (type: DiffLine['type']) => {
    switch (type) {
      case 'added':
        return <Plus className="h-3 w-3 text-green-600" />;
      case 'removed':
        return <Minus className="h-3 w-3 text-red-600" />;
      case 'modified':
        return <Edit className="h-3 w-3 text-blue-600" />;
      default:
        return null;
    }
  };

  const getLineStyle = (type: DiffLine['type']) => {
    switch (type) {
      case 'added':
        return 'bg-green-50 dark:bg-green-950/20 border-l-4 border-green-500';
      case 'removed':
        return 'bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500';
      case 'modified':
        return 'bg-blue-50 dark:bg-blue-950/20 border-l-4 border-blue-500';
      default:
        return 'bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const stats = useMemo(() => {
    const added = diffData.changes.filter(c => c.type === 'added').length;
    const removed = diffData.changes.filter(c => c.type === 'removed').length;
    const modified = diffData.changes.filter(c => c.type === 'modified').length;
    return { added, removed, modified };
  }, [diffData]);

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            {title}
          </CardTitle>
          {filename && (
            <Badge variant="outline">{filename}</Badge>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Plus className="h-3 w-3 text-green-600" />
            {stats.added} additions
          </span>
          <span className="flex items-center gap-1">
            <Minus className="h-3 w-3 text-red-600" />
            {stats.removed} deletions
          </span>
          {stats.modified > 0 && (
            <span className="flex items-center gap-1">
              <Edit className="h-3 w-3 text-blue-600" />
              {stats.modified} modifications
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {diffData.hunks.map((hunk, hunkIndex) => (
          <div key={hunkIndex} className="space-y-1">
            <div className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">
              {hunk.header}
            </div>
            <div className="font-mono text-sm border rounded overflow-hidden">
              {hunk.lines.map((line, lineIndex) => (
                <div
                  key={lineIndex}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1 text-xs',
                    getLineStyle(line.type)
                  )}
                >
                  <div className="w-6 flex justify-center">
                    {getLineIcon(line.type)}
                  </div>
                  <div className="w-8 text-right text-muted-foreground">
                    {line.oldLineNumber || line.newLineNumber || line.lineNumber}
                  </div>
                  <div className="flex-1 whitespace-pre-wrap font-mono">
                    {line.content || ' '}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {diffData.changes.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No changes detected
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default VersionDiffViewer;